// ADR 16.9.26n §2 / 16.9.26r §2 — the content column, and the cells beside it.
//
// Extracted from `AdaptiveShell.tsx` (16.9.26n's Consequences budgeted the
// split) so the dock has a cell to be appended to. Two invariants hold in
// every branch below and are the reason the branches are shaped as they are:
//
//   • the `<Outlet>` keeps its parent and its sibling index when the dock
//     opens or closes — the wrapper stays the same element with a different
//     class and the dock is **appended** after the outlet's cell, so React
//     reconciles the page in place (AC4: a half-typed exercise answer
//     survives opening the tutor);
//   • the dock is a cell of the body's grid, never a sibling of the frame's
//     three children (`AdaptiveShell.tsx:9–14`).
//
// At `single_pane` the same `TutorDock` renders as a fixed bottom sheet
// instead of a cell, inside the same wrapper, after the same outlet.
//
// ADR 7.10.26v §4 — the dock now sits inside `CompanionPage`, which is index 2
// in both branches (index 1 is `null` in the single column). Outside two pages
// the host is `display: contents`, so the dock's column still meets the `auto`
// track; at `TwoPages` the single column becomes the two-page grid and the
// host is its trailing track.

import { lazy, Suspense, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useMatch } from 'react-router-dom'

import { gated } from '../app/wasmGate'
import { CompanionContents } from '../features/companion/CompanionContents'
import { useCompanion } from '../features/companion/CompanionProviders'
import type { SurfaceHost } from '../features/companion/hosts'
import { IconButton } from '../ui/controls'
import { Skeleton } from '../ui/Skeleton'
import { PageFrame } from './frame/PageFrame'
import { useRouteFrame } from './frame/routeFrame'
import { CompanionPage } from './spread/CompanionPage'
import { PAGED, type SpreadValue } from './spread/readSpread'
import { useCollapsedChrome } from './useCollapsedChrome'
import { useSpreadValue } from './windowClassContext'

// ADR 25.9.26f §3 — these cells reach the wasm facade, and this shell is on the
// boot graph, so each comes in through the gate (§4). `PlanListPage` shares
// its chunk with the `plans` route; the dock's own lazy edge moved with it to
// `CompanionContents` (7.10.26w §2). Each gets its own Suspense boundary
// *inside* the slot it already had, so the `<Outlet>`'s sibling index never moves.
const PlanListPage = lazy(async () => ({
  default: await gated(() => import('../features/plans/PlanListPage'), 'PlanListPage'),
}))
const AppSlot = lazy(async () => ({ default: await gated(() => import('../features/apps/AppSlot'), 'AppSlot') }))

/** ADR 30.9.26ai §9 — the empty detail pane: an installed app's card, else 5.9.26f AC1's prompt. */
function EmptyDetail() {
  const { t } = useTranslation()
  const prompt = <p className="text-body-sm text-ink-muted">{t('lesson_plan_select_prompt')}</p>
  return <Suspense fallback={prompt}><AppSlot slot="plans.empty_detail" fallback={prompt} /></Suspense>
}

/** The list cell while its chunk loads: the same wrapper, so scoping by it holds. */
function ListCellLoading() {
  return (
    <div data-testid="pane-list" className="w-[22rem]">
      <Skeleton className="h-32 w-full" />
    </div>
  )
}

/**
 * ADR 7.10.26v §4 / 7.10.26w §2 — index 2 in every branch: the companion host,
 * and inside it the four fixed slots (the dock cell moved there).
 */
function CompanionCell() {
  return (
    <CompanionPage>
      <CompanionContents />
    </CompanionPage>
  )
}

/** ADR 16.9.26n §2 — the plan list's collapse, remembered beside the sidebar's. */
const PLAN_LIST_COLLAPSE_KEY = 'lingara.plan_list.collapsed'

/**
 * The single column's classes per dock cell (ADR 24.9.26l §5 adds the sheet's).
 * ADR 28.9.26c §3: vertical padding is Android's 16; the horizontal stays M3's
 * 16 compact / 24 medium-and-up margin. ADR 30.9.26k §3: the sheet's padding
 * reads the dock's adjustable height, today's half viewport as its fallback.
 */
const SINGLE_COLUMN_GRID: Record<Arrangement, string> = {
  column: 'grid grid-cols-[minmax(0,1fr)_auto] gap-8 px-6 py-4',
  sheet: 'px-4 pt-4 pb-[var(--dock-sheet-h,50vh)]',
  none: 'px-4 py-4',
  // ADR 7.10.26v §4 — no padding, so the boundary between the tracks is the
  // fold's physical edge; each page pads inside its own track. The template
  // is inline, from `SpreadValue`'s px. `min-h` lets the chrome scroll away.
  paged: 'grid p-0 min-h-[100dvh]',
}

/** ADR 7.10.26v §4 — the leading page's own margin: only when the wrapper does not pad. */
const LEADING_PAD: Record<Arrangement, string> = { column: '', sheet: '', none: '', paged: 'px-4 py-4' }

type Arrangement = 'column' | 'sheet' | 'none' | 'paged'

/** ADR 7.10.26w §2 — the dock's host as the wrapper's arrangement (`page` only occurs paged). */
const ARRANGEMENT: Record<SurfaceHost, Arrangement> = { column: 'column', sheet: 'sheet', page: 'paged' }

interface SingleColumn {
  className: string
  style: CSSProperties | undefined
  pad: string
}

/**
 * ADR 7.10.26v §4 — the wrapper's classes, inline tracks and the leading pad.
 * ADR 7.10.26w §2 — the key is `paged` whenever the spread is, whatever the
 * dock is doing: the tutor is then in the companion track and covers nothing,
 * so v's interim sheet padding is gone.
 */
function singleColumn(spread: SpreadValue, dockDrawn: SurfaceHost | null): SingleColumn {
  if (!PAGED[spread.layout]) {
    const arrangement = dockDrawn === null ? 'none' : ARRANGEMENT[dockDrawn]
    return { className: SINGLE_COLUMN_GRID[arrangement], style: undefined, pad: LEADING_PAD[arrangement] }
  }
  return {
    className: SINGLE_COLUMN_GRID.paged,
    style: { gridTemplateColumns: `minmax(0,1fr) ${spread.companionPx}px`, columnGap: `${spread.gapPx}px` },
    pad: LEADING_PAD.paged,
  }
}

/**
 * ADR 16.9.26n §2 — the left cell of the two-pane grid, in one of three states.
 *
 * `data-testid="pane-list"` sits on the wrapper in **every** state, open or
 * collapsed, so the tests that scope by it hold. On bare `/plans` the outlet
 * *is* the list: `onCollapse` is not passed, the control does not render, and
 * a stored `collapsed = true` is ignored for render — a list you cannot see on
 * a page whose only content is that list is a blank page. Only once a plan is
 * selected does the shell mount the list itself, with the control, and the
 * collapsed strip is a door and nothing else: not a mini-list.
 *
 * The strip is a `<section aria-label>` named as `PlanListPage`'s own
 * `<section>` is, because the page now has two buttons named `tutor_contract`
 * (the sidebar's and the list's) and two named `tutor_expand` once both are
 * folded; the landmark is what lets a screen reader announce which.
 */
function ListCell({
  selected,
  collapsed,
  onToggle,
}: {
  selected: boolean
  collapsed: boolean
  onToggle: (collapsed: boolean) => void
}) {
  const { t } = useTranslation()
  if (!selected) {
    return (
      <div data-testid="pane-list" className="w-[22rem]">
        <Outlet />
      </div>
    )
  }
  if (collapsed) {
    return (
      <section data-testid="pane-list" aria-label={t('lesson_plans_title')} className="w-12">
        <IconButton icon="list_alt" label={t('tutor_expand')} onClick={() => onToggle(false)} />
      </section>
    )
  }
  return (
    <Suspense fallback={<ListCellLoading />}>
      <div data-testid="pane-list" className="w-[22rem]">
        <PlanListPage onCollapse={() => onToggle(true)} />
      </div>
    </Suspense>
  )
}

/**
 * ADR 16.9.26r §2 — an open dock on a dock route, and where it is drawn.
 * ADR 7.10.26w §2 — read from `CompanionProviders`, the same value the cell
 * inside the companion host reads, so the grid and the cell cannot disagree.
 */
function useDockCell(): SurfaceHost | null {
  return useCompanion().dockDrawn
}

/**
 * The content column: one pane, or §4's list beside its detail.
 *
 * `useMatch('/plans/:id')` also matches `/plans/new`, which is the create form
 * and not a plan — excluded here rather than in a second route, because the
 * router is 5.9.26q's and this shell does not get to change it.
 */
export function ShellBody({ listDetail }: { listDetail: boolean }) {
  const onList = useMatch('/plans') !== null
  const detail = useMatch('/plans/:id')
  const selected = detail !== null && detail.params.id !== 'new'
  const split = listDetail && (onList || selected)
  const [listCollapsed, setListCollapsed] = useCollapsedChrome(PLAN_LIST_COLLAPSE_KEY, 'plan_list')
  const dockCell = useDockCell()
  const frame = useRouteFrame()
  const spread = useSpreadValue()

  if (!split) {
    // The single column becomes a two-track grid with the dock as its trailing
    // cell; the cell's cap is the route's frame (29.9.26al §3), a `PageFrame`
    // that stays the outlet's parent either way.
    // ADR 24.9.26l §5 — an open sheet covers the bottom of the window (its
    // height adjustable since 30.9.26k), so the column gains that height as
    // padding: the last control can scroll clear of it. A class on this same
    // node, so the outlet's parent is unchanged.
    const column = singleColumn(spread, dockCell)
    return (
      <div className={column.className} style={column.style}>
        <PageFrame width={frame} className={column.pad}>
          <Outlet />
        </PageFrame>
        {null}
        <CompanionCell />
      </div>
    )
  }
  // On bare `/plans` the outlet *is* the list, so it stays the left column and
  // the right is 5.9.26f AC1's prompt, or an installed app's card. Only once a plan is selected does the
  // shell mount the list itself and hand the outlet the right-hand column.
  // The `auto` track takes the cell's own width (16.9.26n §2): 22rem open,
  // the strip's `w-12` collapsed — and the dock's own width as a third track.
  const columns = dockCell === 'column' ? 'grid-cols-[auto_minmax(0,1fr)_auto]' : 'grid-cols-[auto_minmax(0,1fr)]'
  return (
    <div className={`grid ${columns} gap-8 px-6 py-4`}>
      <ListCell selected={selected} collapsed={listCollapsed} onToggle={setListCollapsed} />
      <div data-testid="pane-detail">
        {selected ? <Outlet /> : <EmptyDetail />}
      </div>
      <CompanionCell />
    </div>
  )
}
